import { useCallback, useEffect, useMemo, useState } from "react";

import { Select, Checkbox, SelectEvent } from "@king-ict/eupisi-ui/components";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { FormCard } from "../../../components";
import {
  DocumentCommittee,
  DocumentCommitteeEmployee,
} from "../../../models/Request";
import { formatDocumentCommitteeEmployee } from "../CommitteeDecision";
import { getSelectedEmployeeIds } from "../utils";
import { getUpdatedCommittees } from "./utils";

interface IProps {
  committees: DocumentCommittee[];
  onCommitteeChange: (documentCommittees: DocumentCommittee[]) => void;
  initialDocumentCommittees: DocumentCommittee[];
  isEditing: boolean;
}

export const CommissionSelection = ({
  committees,
  onCommitteeChange,
  initialDocumentCommittees,
  isEditing,
}: IProps) => {
  const { t } = useTranslation();
  const [selectedCommitteeId, setSelectedCommitteeId] = useState<number>();
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<number[]>([]);
  const [currentEmployees, setCurrentEmployees] = useState<
    DocumentCommitteeEmployee[]
  >([]);

  const updateCommitteesWithSelection = useCallback(
    (committeeId?: number, employeeIds: number[] = []) => {
      const updatedCommittees = getUpdatedCommittees(
        !!committees.length ? committees : initialDocumentCommittees,
        committeeId,
        employeeIds
      );

      onCommitteeChange(updatedCommittees);
    },
    [committees, initialDocumentCommittees, onCommitteeChange]
  );

  const selectDefaultCommitteeEmployees = useCallback(
    (_selectedCommitteeId?: number) => {
      const findCommittee = (committee: DocumentCommittee) =>
        _selectedCommitteeId
          ? committee.committeeId === _selectedCommitteeId
          : committee.isCommitteeSelected;

      const selectedCommittee =
        initialDocumentCommittees.find(findCommittee) ||
        initialDocumentCommittees[0];

      setSelectedCommitteeId(selectedCommittee.committeeId);
      setCurrentEmployees(selectedCommittee.committeeEmployees);

      const currentSelectedEmployeeIds =
        getSelectedEmployeeIds(selectedCommittee);

      setSelectedEmployeeIds(currentSelectedEmployeeIds);
      updateCommitteesWithSelection(
        selectedCommittee.committeeId,
        currentSelectedEmployeeIds
      );

      return currentSelectedEmployeeIds;
    },
    [initialDocumentCommittees]
  );

  // Initialize selected committee and employees
  useEffect(() => {
    selectDefaultCommitteeEmployees();
  }, [initialDocumentCommittees, isEditing, selectDefaultCommitteeEmployees]);

  const handleCommitteeChange = (event: React.ChangeEvent<SelectEvent>) => {
    const { value } = event.target;
    const newCommitteeId = value ? Number(value) : undefined;

    const currentSelectedEmployeeIds =
      selectDefaultCommitteeEmployees(newCommitteeId);

    updateCommitteesWithSelection(newCommitteeId, currentSelectedEmployeeIds); // Reset employee selection when committee changes
  };

  const handleEmployeeChange = (employeeId: number, checked: boolean) => {
    const newSelectedEmployees = checked
      ? [...selectedEmployeeIds, employeeId]
      : selectedEmployeeIds.filter((id) => id !== employeeId);

    setSelectedEmployeeIds(newSelectedEmployees);
    updateCommitteesWithSelection(selectedCommitteeId, newSelectedEmployees);
  };

  const selectData = useMemo(() => {
    const committeesToSelect = !!committees.length
      ? committees
      : initialDocumentCommittees;

    return {
      keyProp: "value",
      valueProp: "value",
      textProp: "label",
      data: committeesToSelect.map(({ committeeId, committeePeriod }) => ({
        value: committeeId,
        label: committeePeriod,
      })),
    };
  }, [committees, initialDocumentCommittees]);

  return (
    <FormCard cardTitle={t("expertCommission.pageNameEdit")}>
      <Row>
        <Col md={12}>
          <Select
            label={t("expertCommission.role")}
            value={selectedCommitteeId}
            onChange={handleCommitteeChange}
            selectData={selectData}
            disabled={!isEditing}
          />
        </Col>
      </Row>
      {!!currentEmployees.length && (
        <Row>
          <Col md={12}>
            {currentEmployees.map((employee) => (
              <Checkbox
                key={employee.committeeEmployeeId}
                label={formatDocumentCommitteeEmployee(employee, t)}
                checked={selectedEmployeeIds.includes(
                  employee.committeeEmployeeId
                )}
                onChange={(e) =>
                  handleEmployeeChange(
                    employee.committeeEmployeeId,
                    e.target.checked
                  )
                }
                plaintext={!isEditing}
              />
            ))}
          </Col>
        </Row>
      )}
    </FormCard>
  );
};
